<script setup lang="ts">
  import { inject, useCssModule } from 'vue';

  import { TABLE_INJECTION } from '../Table/Table.vue';

  export interface TableCellProps {
    /**
     * If true, displays the cell as a control (vs data) cell without a background color on mobile when Table is in the stacked layout
     */
    isControl?: boolean;
    /**
     * If true, displays the header inside the cell on mobile and tablet screens when Table is in the stacked layout
     */
    mobileHeader?: string;
  }

  const props = withDefaults(defineProps<TableCellProps>(), {
    isControl: false,
    mobileHeader: '',
  });
  const classes = useCssModule();

  const tableInjection = inject(TABLE_INJECTION.key);
  if (!tableInjection) {
    throw new Error('TableCell must be used within a Table component');
  }

  const { density, hasActions, layout } = tableInjection;
</script>

<template>
  <td
    class="stash-table-cell"
    data-test="stash-table-cell"
    :class="[
      classes.root,
      classes[`layout--${layout}`],
      {
        [classes['root--density-compact']]: density === 'compact',
        [classes['root--density-comfortable']]: density === 'comfortable',
        [classes['has-actions']]: hasActions,
        'p-3': layout === 'scroll',
        'p-1.5': layout === 'stack',
        'lg:p-3': density === 'compact',
        'lg:px-3 lg:py-6': density === 'comfortable',
        [classes['is-control']]: props.isControl,
      },
    ]"
  >
    <div v-if="props.mobileHeader && layout === 'stack'" class="text-xs font-medium text-ice-900 lg:hidden">
      {{ props.mobileHeader }}
    </div>
    <!-- @slot default -->
    <slot></slot>
  </td>
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .root {
      border: 0;
    }

    .layout--scroll {
      background-color: var(--color-white);
      border: none;
      border-bottom: 1px solid var(--color-ice-200);
    }

    .layout--scroll.has-actions:last-of-type {
      text-align: center;
    }

    .layout--stack {
      background-color: var(--color-ice-100);
      border-radius: var(--radius-sm);
    }

    .layout--stack.has-actions:last-of-type {
      background-color: transparent;
      position: absolute;
      right: 0;
      top: 0;
    }

    .layout--stack.is-control {
      background-color: transparent;
    }

    /* On large screens, layout--stack should match layout--scroll */
    @media (width >= theme(--breakpoint-lg)) {
      .layout--stack {
        background-color: var(--color-white);
        border-bottom: 1px solid var(--color-ice-200);
        border-radius: initial;
      }

      .layout--stack.has-actions:last-of-type {
        background-color: var(--color-white);
        position: initial;
        right: initial;
        text-align: center;
        top: initial;
      }

      .layout--stack.is-control {
        background-color: var(--color-white);
      }

      .root--density-comfortable:last-of-type:not(.has-actions) {
        padding-right: 24px;
      }

      .root--density-comfortable:first-of-type:not(
          :global(.stash-table-header-row__selection-cell),
          :global(.stash-table-row__selection-cell),
          :global(.stash-table-row__toggle-expansion-cell),
          :global(.stash-table-cell__is-selection-cell),
          :global(.stash-table-cell__is-expansion-cell)
        ) {
        padding-left: 24px;
      }
    }
  }
</style>
